import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { atxIsMobile$ } from '@epicuro-next/ataraxia/breakpoint';
import { ModalService } from '@epicuro-next/ataraxia/modal';
import {
  mapMessage,
  MessagePosition,
  SnackBarService,
} from '@epicuro-next/ataraxia/snack-bar';
import {
  FullBasketManagerConfigurationKeys,
  FullBasketManagerConfigurations,
  FULL_PAGE_BASKET_MANAGER_NAMESPACE,
} from '@epicuro-next/constants/rule-engine/basket-manager-rules';
import { OnboardingTarget } from '@epicuro-next/constants/rule-engine/onboarding-rules';
import {
  SellerRulesConfigurationKeys,
  SellerToolsConfiguration,
  sellerToolsConfigurationDefaultValues,
  SELLER_TOOLS_FEATURE_NAME,
} from '@epicuro-next/constants/rule-engine/seller-tools';
import {
  CBasketConfigurationKeys,
  CBASKET_NAMESPACE,
} from '@epicuro-next/data/baskets';
import {
  DEFAULT_IS_ENABLED_ORDER_BOOK_RULE,
  OrderBookConfigurationKeys,
  OrderBookConfigurations,
  ORDER_BOOK_NAMESPACE,
} from '@epicuro-next/data/order-book';
import { OnboardingComponent } from '@epicuro-next/features/onboarding';
import { UploadBasketModalComponent } from '@epicuro-next/features/upload-basket';
import { APP_PRIVATE_PATHS } from '@epicuro-next/navigation/constants';
import {
  ConnectConfigurationKeys,
  NAMESPACE_CONNECT,
  selectConfigurationValueFrom,
  selectContactsUserMenu,
  selectInvoicesPageEnabledRule,
  selectUserMenuBadgesConfig,
} from '@epicuro-next/platform/configuration';
import {
  selectIsCMIEnabled,
  selectIsVMIEnabled,
} from '@epicuro-next/state/accounts-state';
import { selectBestPerformingDashboard } from '@epicuro-next/state/analytics-entities';
import { CBasketConfigurations } from '@epicuro-next/state/basket-state';
import {
  selectConsumerTutorialLink,
  selectSalesTutorialLink,
  selectShowConsumerOnboarding,
  selectShowSalesOnboarding,
  setActiveOnboarding,
} from '@epicuro-next/state/onboarding';
import { selectShowMediaRelationsRule } from '@epicuro-next/state/p-basket-state';
import { selectAppVersion } from '@epicuro-next/state/session-state';
import {
  logout,
  selectIsAssortmentsUser,
  selectIsSalesRepOrAdmin,
} from '@epicuro-next/state/user-state';
import {
  faCartPlus,
  faFolderGear,
  faRightFromBracket,
  faUserLock,
} from '@fortawesome/pro-solid-svg-icons';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay, take, tap } from 'rxjs/operators';

import {
  UserMenuRoute,
  UserMenuRouterService,
} from '../service/user-menu-router.service';
import { VmiDdrDownloadModalComponent } from '../vmi-ddr-download-modal/vmi-ddr-download-modal.component';

import {
  badgeLabels,
  BadgeMenuItem,
  BadgeType,
  MenuItem,
} from './menu-item.model';

const COPY_SUCCESS = $localize`:Pulse|Copy App version to clipboard success message@@copyAppVersionToClipboardSuccess:Version copied`;
const COPY_FAILED = $localize`:Pulse|Copy App version to clipboard failed message@@copyAppVersionToClipboardFailed:Failed to copy version`;

@Component({
  selector: 'epc-user-menu',
  templateUrl: './user-menu.component.html',
  styleUrls: ['./user-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UserMenuComponent {
  @Output()
  public closeMenu = new EventEmitter<void>();

  public orderBookEnabled$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        OrderBookConfigurationKeys.isEnabled,
        OrderBookConfigurations
      >(
        ORDER_BOOK_NAMESPACE,
        OrderBookConfigurationKeys.isEnabled,
        DEFAULT_IS_ENABLED_ORDER_BOOK_RULE,
      ),
    ),
  );

  public isBestPerformingDashboardEnabled$ = this.store
    .select(selectBestPerformingDashboard)
    .pipe(
      map(
        (bestPerformingDashboardSettings) =>
          bestPerformingDashboardSettings.showUserMenuItem,
      ),
    );

  public showMediaRelationsRule$ = this.store
    .select(selectShowMediaRelationsRule)
    .pipe(map((rule) => Boolean(rule)));

  public readonly isCMIEnabled$ = this.store.pipe(select(selectIsCMIEnabled));
  public readonly isVMIEnabled$ = this.store.pipe(select(selectIsVMIEnabled));
  public readonly isInvoicesEnabled$ = this.store.pipe(
    select(selectInvoicesPageEnabledRule),
  );

  protected contactsUserMenu$ = this.store.select(selectContactsUserMenu);

  private isUploadBasketEnabled$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        CBasketConfigurationKeys.UploadSDOBasketEnabled,
        CBasketConfigurations
      >(
        CBASKET_NAMESPACE,
        CBasketConfigurationKeys.UploadSDOBasketEnabled,
        false,
      ),
    ),
  );

  private readonly itemBadgesConfig$ = this.store.pipe(
    select(selectUserMenuBadgesConfig),
    map((config: { [key: string]: BadgeMenuItem }) => {
      return Object.fromEntries(
        Object.entries(config).map(([key, value]) => [
          key,
          {
            ...value,
            text:
              badgeLabels[value.text.toLowerCase() as BadgeType] ?? value.text,
          },
        ]),
      );
    }),
  );

  private isFullPageBasketManager$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        FullBasketManagerConfigurationKeys.FullPageBasketManager,
        FullBasketManagerConfigurations
      >(
        FULL_PAGE_BASKET_MANAGER_NAMESPACE,
        FullBasketManagerConfigurationKeys.FullPageBasketManager,
        false,
      ),
    ),
  );

  private isDesktop$ = atxIsMobile$.pipe(
    map((isMobile) => !isMobile),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public isMobile$ = atxIsMobile$;

  public showMediaRelations$ = combineLatest([
    this.showMediaRelationsRule$,
    this.store.select(selectIsSalesRepOrAdmin),
    this.isDesktop$,
  ]).pipe(
    map(
      ([rule, isSalesRepOrAdmin, isDesktop]) =>
        rule && isSalesRepOrAdmin && isDesktop,
    ),
  );

  private isSellerTutorialEnabled$ = combineLatest([
    this.store.pipe(select(selectShowSalesOnboarding)),
    this.isDesktop$,
  ]).pipe(
    map(([showSalesOnboarding, isDesktop]) => showSalesOnboarding && isDesktop),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private isConsumerTutorialEnabled$ = combineLatest([
    this.store.pipe(select(selectShowConsumerOnboarding)),
    this.isDesktop$,
  ]).pipe(
    map(
      ([showConsumerOnboarding, isDesktop]) =>
        showConsumerOnboarding && isDesktop,
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private readonly consumerTutorialLink$ = this.store.pipe(
    select(selectConsumerTutorialLink),
  );

  private readonly salesTutorialLink$ = this.store.pipe(
    select(selectSalesTutorialLink),
  );

  private isSellerToolsEnabled$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        SellerRulesConfigurationKeys.BudgetEnabled,
        SellerToolsConfiguration
      >(
        SELLER_TOOLS_FEATURE_NAME,
        SellerRulesConfigurationKeys.BudgetEnabled,
        sellerToolsConfigurationDefaultValues[
          SellerRulesConfigurationKeys.BudgetEnabled
        ],
      ),
    ),
  );

  private readonly isBasketReminderEnabled$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        SellerRulesConfigurationKeys.BasketReminderEnabled,
        SellerToolsConfiguration
      >(
        SELLER_TOOLS_FEATURE_NAME,
        SellerRulesConfigurationKeys.BasketReminderEnabled,
        sellerToolsConfigurationDefaultValues[
          SellerRulesConfigurationKeys.BasketReminderEnabled
        ],
      ),
    ),
  );

  public links: MenuItem[] = [
    {
      icon: faCartPlus,
      label: $localize`:userMenu@@menuCreateNewBasket:Create new basket`,
      onClick: () => this.go('UserAccounts'),
      e2e: 'create-new-basket',
      visible: of(true),
    },
    {
      icon: faFolderGear,
      label: $localize`:userMenu|Text for the seller tools button@@sellerToolsButton:Seller Tools`,
      e2e: 'sellerTools',
      onClick: () =>
        this.navigateTo([
          APP_PRIVATE_PATHS.sellerTools.path,
          APP_PRIVATE_PATHS.budget.path,
        ]),
      visible: this.isSellerToolsEnabled$,
    },
    {
      icon: ['fas', 'list-tree'],
      label: $localize`:userMenu|Text for the basket manager button@@basketManagerButton:Basket Manager`,
      onClick: () => this.navigateToBasketManager(),
      e2e: 'switchBasket',
      visible: of(true),
    },
    {
      icon: ['fas', 'bell-on'],
      label: $localize`:userMenu|Text for the seller tools basket reminder@@sellerToolsBasketReminderButton:Basket Reminder`,
      e2e: 'basketsReminder',
      onClick: () =>
        this.navigateTo([
          APP_PRIVATE_PATHS.sellerTools.path,
          APP_PRIVATE_PATHS.basketReminder.path,
        ]),
      visible: this.isBasketReminderEnabled$,
    },
    {
      icon: ['fas', 'upload'],
      label: $localize`:userMenu|Text for the upload sdo basket@@sdoBasketUploadButtonText:Upload basket`,
      onClick: () => this.openUploadBasketModal(),
      e2e: 'upload-sdo-basket',
      visible: this.isUploadBasketEnabled$,
    },
    {
      icon: ['fas', 'book-bookmark'],
      label: $localize`:UserMenu|Order book button text @@userMenuOrderBookButtonText:Order Book`,
      onClick: () => this.navigateTo([APP_PRIVATE_PATHS.orderBook.path]),
      e2e: 'order-book-menu-btn',
      visible: this.orderBookEnabled$,
    },
    {
      icon: ['fas', 'file-invoice'],
      label: $localize`:UserMenu|Invoices button text @@userMenuInvoicesButtonText:Invoices`,
      onClick: () => this.navigateTo([APP_PRIVATE_PATHS.invoices.path]),
      e2e: 'invoices-menu-btn',
      visible: this.isInvoicesEnabled$,
    },
    {
      icon: ['fas', 'repeat'],
      label: $localize`:UserMenu|My NOOS and Replenishment button text @@userMenuMyNoosAndReplenishmentButtonText:My NOOS & Replenishments`,
      onClick: () => this.navigateTo([APP_PRIVATE_PATHS.cmi.path]),
      e2e: 'replenishment-menu-btn',
      visible: this.isCMIEnabled$,
    },
    {
      icon: ['fas', 'file-arrow-down'],
      label: $localize`:UserMenu|DDR report button text @@userMenuVMIDDRButtonText:VMI/DDR`,
      onClick: () => this.openVmiDdrModal(),
      e2e: 'ddr-report-menu-btn',
      visible: this.isVMIEnabled$,
    },
    {
      icon: ['fas', 'trophy-star'],
      label: $localize`:UserMenu|Best performing dashboard button text @@userMenuBestPerformingDashboardButtonText:Best Performing Dashboard`,
      onClick: () => this.navigateTo([APP_PRIVATE_PATHS.dashboard.path]),
      e2e: 'best-performing-menu-btn',
      visible: this.isBestPerformingDashboardEnabled$,
    },
    {
      icon: ['fas', 'photo-film'],
      label: $localize`:UserMenu|Media relations dashboard button text @@userMenuMediaRelationsDashboardButtonText:Media Relations`,
      onClick: () => this.navigateTo([APP_PRIVATE_PATHS.mediaRelations.path]),
      e2e: 'media-relations-menu-btn',
      visible: this.showMediaRelations$,
    },
    {
      icon: ['fas', 'bars-filter'],
      label: $localize`:assortmentManager@@userMenuAssortmentManagerLink:Assortments`,
      onClick: () => this.go('AssortmentManager'),
      e2e: 'assortment-manager',
      visible: this.store.pipe(select(selectIsAssortmentsUser)),
    },
    {
      icon: ['fas', 'square-info'],
      label: $localize`:userMenu@@userMenuConsumerOnboardingLink:Buyer Tutorial`,
      onClick: () => this.openConsumerOnboarding(),
      e2e: 'consumer-onboarding-btn',
      visible: this.isConsumerTutorialEnabled$,
    },
    {
      icon: ['fas', 'square-info'],
      label: $localize`:userMenu@@userMenuConsumerOnboardingLink:Buyer Tutorial`,
      onClick: () => this.openConsumerOnboardingLink(),
      e2e: 'consumer-onboarding-link',
      visible: this.consumerTutorialLink$.pipe(map(Boolean)),
    },
    {
      icon: ['fas', 'square-question'],
      label: $localize`:userMenu@@userMenuSalesRedOnboardingLink:Seller Tutorial`,
      onClick: () => this.openSalesRepOnboarding(),
      e2e: 'sales-onboarding-btn',
      visible: this.isSellerTutorialEnabled$,
    },
    {
      icon: ['fas', 'square-question'],
      label: $localize`:userMenu@@userMenuSalesRedOnboardingLink:Seller Tutorial`,
      onClick: () => this.openSalesRepOnboardingLink(),
      e2e: 'sales-onboarding-link',
      visible: this.salesTutorialLink$.pipe(map(Boolean)),
    },
    {
      icon: faUserLock,
      label: $localize`:userMenu@@userMenuChangePasswordLink:Change password`,
      onClick: () => this.go('ChangePassword'),
      e2e: 'change-password',
      visible: of(true),
    },
    {
      icon: faRightFromBracket,
      label: $localize`:userMenu@@userMenuLogoutLink:Logout`,
      onClick: this.handleLogout.bind(this),
      e2e: 'logout',
      visible: of(true),
    },
  ].map((item) => ({
    ...item,
    badge: this.getBadgeMenuItem(item.e2e),
  }));

  public connectNamespace = NAMESPACE_CONNECT;
  public connectEnabledFlag = ConnectConfigurationKeys.ConnectEnabled;

  public readonly version$ = this.store.select(selectAppVersion);
  public readonly copyVersionText = $localize`:Pulse|Tooltip text for app version icon@@copyAppVersionIconTooltipText:Copy version`;

  constructor(
    private store: Store,
    private menuRouterService: UserMenuRouterService,
    private router: Router,
    private modalService: ModalService,
    private snackBarService: SnackBarService,
  ) {}

  public getBadgeMenuItem(
    itemId: string,
  ): Observable<BadgeMenuItem | undefined> {
    return this.itemBadgesConfig$.pipe(
      map((config) => {
        return config[itemId];
      }),
    );
  }

  public handleLogout() {
    this.store.dispatch(logout());
  }

  public go(route: UserMenuRoute) {
    this.menuRouterService.go(route);
  }

  public navigateTo(commands: string[]) {
    this.router.navigate(commands, {
      queryParamsHandling: 'merge',
    });
  }

  public navigateToBasketManager() {
    combineLatest([this.isFullPageBasketManager$, this.isDesktop$])
      .pipe(take(1))
      .subscribe(([isFullPageBasketManager, isDesktop]) => {
        if (isFullPageBasketManager && isDesktop) {
          this.router.navigate([APP_PRIVATE_PATHS.basketManager.path], {
            queryParamsHandling: 'merge',
          });
        } else {
          this.go('BasketManager');
        }
      });
  }

  public openUploadBasketModal() {
    this.modalService.open(UploadBasketModalComponent, {
      backdrop: 'static',
      scrollable: true,
    });
    this.closeMenu.emit();
  }

  public openVmiDdrModal() {
    this.modalService.open(VmiDdrDownloadModalComponent, {
      backdrop: 'static',
      size: 'sm',
    });
    this.closeMenu.emit();
  }

  private openConsumerOnboarding() {
    this.store.dispatch(
      setActiveOnboarding({ target: OnboardingTarget.Consumer }),
    );
    this.openOnboardingModal();
  }

  private openConsumerOnboardingLink() {
    this.consumerTutorialLink$
      .pipe(
        take(1),
        tap((url) => {
          window.open(url, '_blank');
        }),
      )
      .subscribe();
  }

  private openSalesRepOnboardingLink() {
    this.salesTutorialLink$
      .pipe(
        take(1),
        tap((url) => {
          window.open(url, '_blank');
        }),
      )
      .subscribe();
  }

  private openSalesRepOnboarding() {
    this.store.dispatch(
      setActiveOnboarding({ target: OnboardingTarget.SalesRep }),
    );
    this.openOnboardingModal();
  }

  public openOnboardingModal() {
    this.modalService.open(OnboardingComponent, {
      backdrop: 'static',
      scrollable: false,
      size: 'xl',
      centered: true,
    });
    this.closeMenu.emit();
  }

  public versionCopiedToClipboard(success: boolean) {
    const messageData = mapMessage(success, COPY_SUCCESS, COPY_FAILED);
    this.snackBarService.show(
      messageData.msg,
      MessagePosition.BOTTOM,
      messageData.msgType,
    );
  }
}
